import { useCallback, useEffect, useRef, useState } from 'react';
import { coursesApi } from '../services/coursesApi';
import { applyCourseCreateEvent, INITIAL_COURSE_CREATE_STREAM } from './courseCreateStreamReducer';

/**
 * Streams course creation via SSE (see docs/sse-course-creation-api-contract.md
 * and courseCreateStreamReducer.js) and exposes a stepper-friendly phase
 * machine: idle -> creating -> done | content_warning | error | connection_lost.
 *
 * No auto-retry on transport failure/stall (unlike quiz grading) — re-POSTing
 * a dropped create risks a duplicate course, so a dropped connection settles
 * into `connection_lost` instead and leaves recovery to the user (check the
 * dashboard) rather than silently retrying.
 */
export function useCreateCourseStream({ studentId } = {}) {
  const [phase, setPhase] = useState('idle');
  const [stages, setStages] = useState(INITIAL_COURSE_CREATE_STREAM.stages);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const abortRef = useRef(null);

  // Abort any in-flight stream on unmount.
  useEffect(() => () => {
    try { abortRef.current?.abort(); } catch { /* noop */ }
  }, []);

  const cancel = useCallback(() => {
    try { abortRef.current?.abort(); } catch { /* noop */ }
  }, []);

  const create = useCallback(async ({ payload }) => {
    setError(null);
    setResult(null);
    setStages(INITIAL_COURSE_CREATE_STREAM.stages);
    setPhase('creating');

    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    const runStream = async () => {
      let acc = INITIAL_COURSE_CREATE_STREAM;
      for await (const evt of coursesApi.createCourseStream({ payload, studentId, signal })) {
        acc = applyCourseCreateEvent(acc, evt);
        setStages(acc.stages);
        if (acc.result || acc.errored) break;
      }
      return acc;
    };

    try {
      const acc = await runStream();

      if (acc.errored) {
        // In-band failure on an otherwise-200 stream. CONTENT_POLICY_VIOLATION
        // gets its own recoverable state ("try a different topic"); everything
        // else (GENERATION_FAILED, and any future in-band code) is a generic,
        // safely-retryable error — the pipeline left nothing partially usable.
        const nextPhase = acc.errored.code === 'CONTENT_POLICY_VIOLATION' ? 'content_warning' : 'error';
        setPhase(nextPhase);
        setError(acc.errored);
        return { phase: nextPhase, error: acc.errored };
      }

      if (acc.result) {
        setResult(acc.result);
        setPhase('done');
        return { phase: 'done', result: acc.result };
      }

      // Defensive: the stream ended (reader.read() returned done) without a
      // terminal `complete`/`error` frame — treat like any other dropped
      // connection.
      const lost = { message: 'Connection lost. Your course may still be finishing — check your dashboard.' };
      setPhase('connection_lost');
      setError(lost);
      return { phase: 'connection_lost', error: lost };
    } catch (err) {
      if (err?.name === 'AbortError') {
        // User cancel or unmount — back to idle, nothing to show.
        setPhase('idle');
        return { phase: 'idle' };
      }

      // Pre-stream HTTP guard failures (400/401/403/404/429) carry a real
      // status code — see the pre-stream table in the contract doc. Surface
      // the raw error (with .code/.status/.data) so callers can branch on
      // err.code the same way they already do for STUDENT_SUBSCRIPTION_REQUIRED.
      if (typeof err?.status === 'number') {
        setPhase('error');
        setError(err);
        return { phase: 'error', error: err };
      }

      // Everything else is a transport failure or a stall (fetch rejected,
      // the reader threw, or sse.js's STREAM_STALLED watchdog fired) — no
      // auto-retry, since re-POSTing risks a duplicate course.
      setPhase('connection_lost');
      setError(err);
      return { phase: 'connection_lost', error: err };
    }
  }, [studentId]);

  return { create, cancel, phase, stages, error, result };
}
